import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useStatuses } from "@/context/StatusContext";
import { PRIORITY_META, PRIORITY_ORDER } from "@/lib/meta";
import JobTimer from "@/components/JobTimer";
import { Noticeboard } from "@/components/Noticeboard";
import { JobEditDialog } from "@/components/JobEditDialog";
import { toast } from "sonner";
import { User, CalendarDays, Building2, GripVertical, Columns3, CalendarClock, Search, MapPin, X } from "lucide-react";

// Solid accent per column colour (header dot)
const ACCENT = {
  yellow: "bg-yellow-400", blue: "bg-blue-400", cyan: "bg-cyan-400",
  purple: "bg-purple-400", emerald: "bg-emerald-400", orange: "bg-orange-400",
  red: "bg-red-500", pink: "bg-pink-400", indigo: "bg-indigo-400", zinc: "bg-zinc-400",
};

const DUE_COLUMNS = [
  { key: "overdue", label: "Overdue", color: "red" },
  { key: "today", label: "Today", color: "orange" },
  { key: "week", label: "This week", color: "blue" },
  { key: "future", label: "Future", color: "zinc" },
];

// Thin left colour stripe per priority
const PRIORITY_STRIPE = {
  low: "border-l-zinc-300",
  medium: "border-l-orange-400",
  high: "border-l-red-500",
};

// Extra emphasis for the time-critical due-date columns
const EMPH = {
  overdue: {
    col: "border-red-300 bg-red-50/70 ring-1 ring-red-200",
    head: "bg-red-100/60 border-red-200",
    title: "text-red-700",
  },
  today: {
    col: "border-amber-300 bg-amber-50/70 ring-1 ring-amber-200",
    head: "bg-amber-100/60 border-amber-200",
    title: "text-amber-700",
  },
};

// Today's date in NZ as YYYY-MM-DD (ISO strings compare lexically)
function nzDateISO(offsetDays = 0) {
  const todayNZ = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Pacific/Auckland", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
  if (!offsetDays) return todayNZ;
  const d = new Date(todayNZ + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function Board() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { statuses, map, metaFor, orderedKeys } = useStatuses();
  const canSeeTimer = user.role === "admin" || user.role === "superadmin";
  const isManager = user.role === "admin" || user.role === "manager";

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [riggers, setRiggers] = useState([]);
  const [editJob, setEditJob] = useState(null);
  const dragRef = useRef(false);

  // Saved views: restore this user's last grouping + filters on first render.
  const prefsKey = `jobq:board:${user.id}`;
  const readPref = (k, d) => {
    try {
      const v = JSON.parse(localStorage.getItem(prefsKey) || "{}")[k];
      return v == null ? d : v;
    } catch {
      return d;
    }
  };
  const [groupBy, setGroupBy] = useState(() => readPref("groupBy", "due")); // "status" | "due"
  const [fRigger, setFRigger] = useState(() => readPref("fRigger", "all"));
  const [fCustomer, setFCustomer] = useState(() => {
    const v = readPref("fCustomer", "");
    return v === "all" ? "" : v; // migrate old dropdown pref to search text
  });
  const [fLocation, setFLocation] = useState(() => readPref("fLocation", "all"));
  const [fPriority, setFPriority] = useState(() => readPref("fPriority", "all"));

  useEffect(() => {
    localStorage.setItem(prefsKey, JSON.stringify({ groupBy, fRigger, fCustomer, fLocation, fPriority }));
  }, [prefsKey, groupBy, fRigger, fCustomer, fLocation, fPriority]);

  // Drag only makes sense when columns represent settable statuses
  const canDrag = isManager && groupBy === "status";

  const load = useCallback((quiet = false) => {
    if (!quiet) setLoading(true);
    api
      .get("/jobs")
      .then((r) => setJobs(r.data))
      .finally(() => {
        if (!quiet) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Managers/admins get the rigger list for assign-from-card
  useEffect(() => {
    if (isManager) api.get("/riggers").then((r) => setRiggers(r.data)).catch(() => {});
  }, [isManager]);

  // Quiet auto-refresh every 60s for wall displays (skipped mid-drag)
  useEffect(() => {
    const id = setInterval(() => {
      if (!dragRef.current) load(true);
    }, 60000);
    return () => clearInterval(id);
  }, [load]);

  const assign = async (jobId, riggerId) => {
    const snapshot = jobs;
    const rig = riggers.find((r) => r.id === riggerId);
    setJobs((js) =>
      js.map((j) => (j.id === jobId ? { ...j, assigned_to: riggerId, assigned_to_name: rig?.name || null } : j))
    );
    try {
      const r = await api.put(`/jobs/${jobId}`, { assigned_to: riggerId });
      setJobs((js) => js.map((j) => (j.id === jobId ? r.data : j)));
      toast.success(riggerId ? `Assigned to ${rig?.name}` : "Unassigned");
    } catch (err) {
      setJobs(snapshot);
      toast.error(apiError(err.response?.data?.detail) || "Could not update assignment");
    }
  };

  const { nzToday, endOfWeek } = useMemo(() => {
    const today = nzDateISO();
    const dow = new Date(new Date().toLocaleString("en-US", { timeZone: "Pacific/Auckland" })).getDay();
    return { nzToday: today, endOfWeek: nzDateISO((7 - dow) % 7) };
  }, []);

  const riggerOptions = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.assigned_to_name).filter(Boolean))).sort(),
    [jobs]
  );
  const locationOptions = useMemo(
    () => Array.from(new Set(jobs.map((j) => j.location).filter(Boolean))).sort(),
    [jobs]
  );

  // Statuses ticked "Hide from Home due-date board" are hidden from the due-date view only.
  const hiddenStatusKeys = useMemo(
    () =>
      statuses
        .filter((s) => s.board_hidden)
        .map((s) => s.key),
    [statuses]
  );

  const filtered = useMemo(() => {
    const q = fCustomer.trim().toLowerCase();
    return jobs.filter(
      (j) =>
        (fRigger === "all" || (j.assigned_to_name || "") === fRigger) &&
        (!q || (j.customer || "").toLowerCase().includes(q)) &&
        (fLocation === "all" || (j.location || "") === fLocation) &&
        (fPriority === "all" || j.priority === fPriority)
    );
  }, [jobs, fRigger, fCustomer, fLocation, fPriority]);

  const dueBucket = useCallback(
    (j) => {
      const d = j.due_date;
      if (!d) return "none";
      if (d < nzToday) return "overdue";
      if (d === nzToday) return "today";
      if (d <= endOfWeek) return "week";
      return "future";
    },
    [nzToday, endOfWeek]
  );

  const columns = useMemo(() => {
    if (groupBy === "status") {
      return orderedKeys().map((key) => ({
        key,
        label: metaFor(key).label,
        color: map[key]?.color || "zinc",
        jobs: filtered.filter((j) => j.status === key),
      }));
    }
    // In due-date view, unassigned jobs get their own group (shown first so it's
    // always visible); assigned jobs fall into their due bucket. Jobs in a
    // "Completed - Dispatch" status are hidden from this view only.
    const visible = filtered.filter((j) => !hiddenStatusKeys.includes(j.status));
    const cols = [
      {
        key: "unassigned",
        label: "Unassigned",
        color: "indigo",
        jobs: visible.filter((j) => !j.assigned_to),
      },
      ...DUE_COLUMNS.map((c) => ({
        ...c,
        jobs: visible.filter((j) => j.assigned_to && dueBucket(j) === c.key),
      })),
    ];
    const none = visible.filter((j) => j.assigned_to && !j.due_date);
    if (none.length) cols.push({ key: "none", label: "No due date", color: "zinc", jobs: none });
    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, filtered, statuses, dueBucket]);

  const move = async (jobId, toStatus) => {
    const job = jobs.find((j) => j.id === jobId);
    if (!job || job.status === toStatus) return;
    const snapshot = jobs;
    setJobs((js) => js.map((j) => (j.id === jobId ? { ...j, status: toStatus } : j)));
    try {
      const r = await api.patch(`/jobs/${jobId}/status`, { status: toStatus, note: "" });
      setJobs((js) => js.map((j) => (j.id === jobId ? r.data : j)));
      toast.success(`“${job.title}” moved to ${metaFor(toStatus).label}`);
    } catch (err) {
      setJobs(snapshot);
      toast.error(apiError(err.response?.data?.detail) || "Could not move this job");
    }
  };

  const onDrop = (colKey) => {
    const id = dragId;
    setDragId(null);
    setOverCol(null);
    if (id) move(id, colKey);
  };

  const selectCls =
    "text-sm border border-zinc-300 rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand";

  return (
    <div className="p-6 lg:p-8 h-full flex flex-col" data-testid="board-page">
      <div className="mb-5 shrink-0">
        <h1 className="font-heading text-3xl font-black tracking-tighter text-zinc-900">Home</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {canDrag
            ? "Drag a job card between columns to update its status."
            : groupBy === "due"
            ? "Jobs grouped by when they're due."
            : "A read-only view of every job grouped by status."}
        </p>
      </div>

      {/* Controls */}
      <div className="mb-5 shrink-0 flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-lg border border-zinc-300 overflow-hidden" data-testid="board-groupby">
          <button
            type="button"
            data-testid="board-groupby-status"
            onClick={() => setGroupBy("status")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold transition-colors ${
              groupBy === "status" ? "bg-brand text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <Columns3 size={15} /> Status
          </button>
          <button
            type="button"
            data-testid="board-groupby-due"
            onClick={() => setGroupBy("due")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold transition-colors border-l border-zinc-300 ${
              groupBy === "due" ? "bg-brand text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <CalendarClock size={15} /> Due date
          </button>
        </div>

        <div className="h-5 w-px bg-zinc-200 hidden sm:block" />

        <select value={fRigger} onChange={(e) => setFRigger(e.target.value)} className={selectCls} data-testid="board-filter-rigger">
          <option value="all">All riggers</option>
          {riggerOptions.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>

        <div className="relative" data-testid="board-filter-customer-wrap">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            type="text"
            value={fCustomer}
            onChange={(e) => setFCustomer(e.target.value)}
            placeholder="Search customer…"
            data-testid="board-filter-customer"
            className="text-sm border border-zinc-300 rounded-lg pl-8 pr-7 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-brand w-44"
          />
          {fCustomer && (
            <button
              type="button"
              data-testid="board-filter-customer-clear"
              onClick={() => setFCustomer("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className="relative inline-flex items-center">
          <MapPin size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <select
            value={fLocation}
            onChange={(e) => setFLocation(e.target.value)}
            className={`${selectCls} pl-8`}
            data-testid="board-filter-location"
          >
            <option value="all">All locations</option>
            {locationOptions.map((loc) => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>

        <select value={fPriority} onChange={(e) => setFPriority(e.target.value)} className={selectCls} data-testid="board-filter-priority">
          <option value="all">All priorities</option>
          {PRIORITY_ORDER.map((p) => (
            <option key={p} value={p}>{PRIORITY_META[p].label}</option>
          ))}
        </select>

        {(fRigger !== "all" || fCustomer !== "" || fLocation !== "all" || fPriority !== "all") && (
          <button
            type="button"
            data-testid="board-filter-clear"
            onClick={() => {
              setFRigger("all");
              setFCustomer("");
              setFLocation("all");
              setFPriority("all");
            }}
            className="text-sm text-brand font-semibold hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-400 font-mono text-sm">Loading…</div>
      ) : (
        <div className="flex-1 min-h-0 flex gap-4">
          <Noticeboard />
          <div className="flex-1 min-w-0 flex gap-4 overflow-x-auto pb-4" data-testid="board-columns">
          {columns.map((col) => {
            const isOver = overCol === col.key;
            const droppable = canDrag;
            const emph = EMPH[col.key];
            return (
              <div
                key={col.key}
                data-testid={`board-column-${col.key}`}
                onDragOver={(e) => {
                  if (!droppable) return;
                  e.preventDefault();
                  if (overCol !== col.key) setOverCol(col.key);
                }}
                onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                onDrop={() => droppable && onDrop(col.key)}
                className={`w-[300px] shrink-0 flex flex-col rounded-xl border transition-colors duration-150 ${
                  isOver ? "border-brand bg-brand-card-blue/40" : emph ? emph.col : "border-zinc-200 bg-zinc-50/60"
                }`}
              >
                <div className={`flex items-center gap-2 px-4 py-3 border-b ${emph ? emph.head : "border-zinc-200"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full ${ACCENT[col.color] || ACCENT.zinc}`} />
                  <h2 className={`text-sm font-bold truncate flex-1 ${emph ? emph.title : "text-zinc-800"}`}>{col.label}</h2>
                  <span className="text-xs font-bold text-zinc-500 bg-white border border-zinc-200 rounded-full px-2 py-0.5 tabular-nums">
                    {col.jobs.length}
                  </span>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
                  {col.jobs.length === 0 && (
                    <div className="text-center text-xs text-zinc-400 py-8 border border-dashed border-zinc-200 rounded-lg">
                      No jobs
                    </div>
                  )}
                  {col.jobs.map((j) => (
                    <div
                      key={j.id}
                      data-testid={`board-card-${j.id}`}
                      draggable={canDrag}
                      onDragStart={() => {
                        if (!canDrag) return;
                        dragRef.current = true;
                        setDragId(j.id);
                      }}
                      onDragEnd={() => {
                        dragRef.current = false;
                        setDragId(null);
                        setOverCol(null);
                      }}
                      onClick={() => (isManager ? setEditJob(j) : navigate(`/jobs/${j.id}`))}
                      className={`group bg-white border border-zinc-200 border-l-4 ${
                        PRIORITY_STRIPE[j.priority] || "border-l-zinc-200"
                      } rounded-lg p-3 shadow-[0_1px_3px_rgba(0,0,0,0.06)] transition-all duration-150 hover:shadow-[0_6px_18px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 cursor-pointer ${
                        canDrag ? "active:cursor-grabbing" : ""
                      } ${dragId === j.id ? "opacity-40" : ""}`}
                    >
                      <div className="flex items-start gap-1.5">
                        {canDrag && (
                          <GripVertical size={15} className="text-zinc-300 group-hover:text-zinc-400 mt-0.5 shrink-0" />
                        )}
                        <div className="font-semibold text-sm text-zinc-900 leading-snug flex-1">{j.title}</div>
                      </div>

                      <div className="mt-2.5 space-y-1.5 text-xs text-zinc-600">
                        {j.customer && (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={13} className="text-zinc-400 shrink-0" />
                            <span className="truncate">{j.customer}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          <User size={13} className="text-zinc-400 shrink-0" />
                          {isManager ? (
                            <select
                              data-testid={`board-assign-${j.id}`}
                              value={j.assigned_to || ""}
                              onClick={(e) => e.stopPropagation()}
                              onMouseDown={(e) => e.stopPropagation()}
                              onChange={(e) => assign(j.id, e.target.value)}
                              className="flex-1 min-w-0 text-xs border border-zinc-300 rounded px-1.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-brand"
                            >
                              <option value="">Unassigned</option>
                              {j.assigned_to && !riggers.some((r) => r.id === j.assigned_to) && (
                                <option value={j.assigned_to}>{j.assigned_to_name || "Assigned"}</option>
                              )}
                              {riggers.map((r) => (
                                <option key={r.id} value={r.id}>{r.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className={j.assigned_to_name ? "" : "text-zinc-400"}>
                              {j.assigned_to_name || "Unassigned"}
                            </span>
                          )}
                        </div>
                        {j.due_date && (
                          <div className="flex items-center gap-1.5">
                            <CalendarDays size={13} className="text-zinc-400 shrink-0" />
                            <span className="tabular-nums">{j.due_date}</span>
                          </div>
                        )}
                      </div>

                      {canSeeTimer && (
                        <div className="mt-2.5 pt-2.5 border-t border-zinc-100 text-xs">
                          <JobTimer job={j} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {statuses.length === 0 && groupBy === "status" && (
            <div className="text-zinc-400 text-sm">No statuses configured for this tenant.</div>
          )}
          </div>
        </div>
      )}

      {isManager && (
        <JobEditDialog
          job={editJob}
          open={!!editJob}
          onOpenChange={(o) => !o && setEditJob(null)}
          onSaved={(upd) => setJobs((js) => js.map((j) => (j.id === upd.id ? upd : j)))}
          onOpenFull={(jobId) => {
            setEditJob(null);
            navigate(`/jobs/${jobId}`);
          }}
        />
      )}
    </div>
  );
}
